import { useEffect, useState } from "react";
import { useMySubscription } from "@/api/hooks";
import { tg } from "@/lib/telegram";
import { FreezeCreditedModal } from "@/components/FreezeCreditedModal";

/**
 * Глобальний детект ПОКУПКИ заморозки (owner 2026-07-22).
 *
 * Флоу: FreezeBuyPanel перед відкриттям WFP пише у localStorage
 * `pending_freeze_invoice` = {days, balance_before, started_at_before}.
 * Після оплати вебхук нараховує дні на freeze_days_balance (підписку НЕ
 * заморожує). Цей watcher (змонтований у корені App) при наявності
 * pending-ключа полить /subscription/me; щойно freeze_days_balance виріс
 * відносно balance_before → показує FreezeCreditedModal і чистить ключ.
 *
 * Дедуп простий, локальний: попап показується на пристрої, з якого юзер
 * платив (ключ живе лише там), TTL 15 хв — як у pending_invoice підписки.
 */
const PENDING_KEY = "pending_freeze_invoice";

export function FreezeCreditedWatcher() {
  const subQuery = useMySubscription();
  const [credited, setCredited] = useState<{
    daysAdded: number;
    balance: number;
  } | null>(null);

  useEffect(() => {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return;
    let pending: {
      days: number;
      balance_before: number;
      started_at_before: number;
    } | null = null;
    try {
      pending = JSON.parse(raw);
    } catch {
      localStorage.removeItem(PENDING_KEY);
      return;
    }
    if (
      !pending ||
      Date.now() - pending.started_at_before > 15 * 60 * 1000
    ) {
      localStorage.removeItem(PENDING_KEY);
      return;
    }

    const check = (data: unknown): boolean => {
      const bal = Number(
        (data as { freeze_days_balance?: number } | undefined)
          ?.freeze_days_balance ?? NaN,
      );
      if (!Number.isFinite(bal) || bal <= pending!.balance_before) return false;
      tg()?.HapticFeedback?.notificationOccurred?.("success");
      setCredited({
        daysAdded: bal - pending!.balance_before,
        balance: bal,
      });
      localStorage.removeItem(PENDING_KEY);
      return true;
    };

    if (check(subQuery.data)) return;

    let attempts = 0;
    const MAX_ATTEMPTS = 30; // 90 сек
    const interval = setInterval(async () => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        clearInterval(interval);
        localStorage.removeItem(PENDING_KEY);
        return;
      }
      try {
        const r = await subQuery.refetch();
        if (check(r.data)) clearInterval(interval);
      } catch {
        /* ignore */
      }
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    (subQuery.data as { freeze_days_balance?: number } | undefined)
      ?.freeze_days_balance ?? null,
  ]);

  if (!credited) return null;
  return (
    <FreezeCreditedModal
      daysAdded={credited.daysAdded}
      balance={credited.balance}
      onClose={() => setCredited(null)}
    />
  );
}
